import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../i18n';
import { homePathFor } from '../i18n/localePath';
import { getCounterpartPath, getLegalFromPath } from '../legal/routes';
import { scrollToTopInstant } from './ScrollToTop';
import LegalContent from './LegalContent';

const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * Full-page legal document (privacy, imprint, cookies).
 * Language is determined by the URL so English and German each have their own link.
 * Title, canonical and description are applied by DocumentHead.
 */
const LegalPage: React.FC = () => {
  const location = useLocation();
  const { t, language, setLanguage } = useLanguage();
  const match = getLegalFromPath(location.pathname);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useIsoLayoutEffect(() => {
    if (match && match.language !== language) {
      setLanguage(match.language);
    }
  }, [match, language, setLanguage]);

  useIsoLayoutEffect(() => {
    (document.activeElement as HTMLElement | null)?.blur();
    scrollToTopInstant();
    headingRef.current?.focus({ preventScroll: true });
    const frame = window.requestAnimationFrame(() => {
      scrollToTopInstant();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname]);

  if (!match) {
    return <Navigate to="/" replace />;
  }

  const counterpart = getCounterpartPath(location.pathname);
  const title = t.legal[match.type].title;

  return (
    <section className="min-h-screen bg-white pt-10 md:pt-14 pb-16 [overflow-anchor:none]" aria-labelledby="legal-title">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <Link
            to={homePathFor(language)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" aria-hidden="true" />
            {t.legal.backToHome}
          </Link>
          {counterpart && (
            <Link
              to={counterpart}
              className="text-sm font-semibold text-gray-600 hover:text-primary-600 transition-colors"
              hrefLang={match.language === 'en' ? 'de' : 'en'}
              lang={match.language === 'en' ? 'de' : 'en'}
            >
              {t.legal.readInOtherLanguage}
            </Link>
          )}
        </div>

        <article>
          <h1
            id="legal-title"
            ref={headingRef}
            tabIndex={-1}
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 outline-none"
          >
            {title}
          </h1>
          <LegalContent type={match.type} />
        </article>
      </div>
    </section>
  );
};

export default LegalPage;
